import React, { useEffect,  useState } from 'react';
import { FaAngleUp, FaAngleDown } from 'react-icons/fa6';
import { LuChevronLeft } from 'react-icons/lu';
import { LuChevronRight } from 'react-icons/lu';
import { PropertyDrawerProps } from '@/pages/property/components/propertyDrawer/types';
import { useSelector } from 'react-redux';
import { RootState } from '@store';
import {  useNavigate } from 'react-router-dom';
import { OwnerShip } from '@/pages/property/components/propertyDrawer/ownership';
import { PersonInfo, StabilizedUnit } from '@interface';
import {
	convertToYearlyUnits,
	formatDate,
	getComunityDistrictLabel,
	getPercentageDifference,
	transformContacts,
} from '@utils';
import { DistrictMap } from '@components';

const council_base_url = process.env.REACT_APP_COUNCIL_BASE_URL;
const state_assembly_base_url = process.env.REACT_APP_STATE_ASSEMBLY_BASE_URL;
const state_senate_base_url = process.env.REACT_APP_STATE_SENSTE_BASE_URL;

export const PropertyDrawer: React.FC<PropertyDrawerProps> = ({
	isOpen: propIsOpen = true,
	onToggle,
}) => {
	const [isOpen, setIsOpen] = useState(propIsOpen);
	const [locations,setLocations ] = useState<any[]>([])
	const [activeSections, setActiveSections] = useState<string[]>([]);
	const [yearlyUnits, setYearlyUnits] = useState<StabilizedUnit[]>([]);
	const [contacts, setContacts] = useState<PersonInfo[]>([]);
	const propertyInfo = useSelector(
		(state: RootState) => state.rootSlice.property.propertyInfo
	);
	const navigate = useNavigate();
	const {
		bbl,
		council,
		cd,
		stateassembly,
		statesenate,
		zipcode,
		unitstotal,
		unitsres,
		yearbuilt,
		ownername,
		unitsrentstabilized,
		rentstabilizationrecord,
		hpdregistrations,
		zonedist1,
		overlay1,
		spdist1,
		builtfar,
		residfar,
		commfar,
		facilfar,
		latitude,
		longitude,
		address,
	} = propertyInfo.data;

	useEffect(() => {
		rentstabilizationrecord &&
			setYearlyUnits(convertToYearlyUnits(rentstabilizationrecord));

		hpdregistrations.length > 0 &&
			setContacts(transformContacts(hpdregistrations[0].contacts));
		
		setLocations([
			{
				Address: address,
				latitude: Number(latitude),
				longitude: Number(longitude),
				BBL: bbl,
				'Zip Code': zipcode,
			},
		]);
	}, [propertyInfo]);

	const toggleDrawer = () => {
		const newState = !isOpen;
		setIsOpen(newState);
		onToggle?.();
	};

	const toggleSection = (section: string) => {
		if (activeSections.includes(section)) {
			setActiveSections(activeSections.filter((s) => s !== section));
		} else {
			setActiveSections([...activeSections, section]);
		}
	};

	function navigaterHandler({ module, id }: { module: string; id: string }) {
		navigate(`/district-dashboard/${module}/${id}`);
	}

	const propertyInfoTab = [
		{ label: 'BBL', value: bbl },
		{
			label: 'Council District',
			value: council,
			link: `${council_base_url}-${council}/`,
			module: 'council',
		},
		{
			label: 'Community District',
			value: getComunityDistrictLabel(cd),
			module: 'community',
		},
		{
			label: 'State Assembly',
			value: stateassembly,
			link: `${state_assembly_base_url}/`,
			module: 'state-assembly',
		},
		{
			label: 'State Senate',
			value: statesenate,
			link: `${state_assembly_base_url}/`,
			module: 'state-senate',
		},
		{ label: 'Zip Code', value: zipcode, module: 'zipcode' },
		{ label: 'Total Units', value: unitstotal },
		{ label: 'Residential Units', value: unitsres },
		{ label: 'Year Built', value: yearbuilt },
	];

	const renderPropertyItem = (
		{ label, value, module, link }: any,
		index: number
	) => (
		<div className="drawer-item-div" key={index}>
			<span className="drawer-text">{label}</span>
			{module ? (
				<div>
					<span
						className="text-link font-bold"
						onClick={() => navigaterHandler({ module, id: value })}
					>
						{value}
					</span>{' '}
					{link && (
						<a
							href={link}
							className="text-link font-bold"
							target="_blank"
							rel="noopener noreferrer"
						>
							(Visit)
						</a>
					)}
				</div>
			) : (
				<span>{value}</span>
			)}
		</div>
	);

	const renderSection = (title: string, content: React.ReactNode) => (
		<div className=" border-gray-200 border-b-0 sticky top-0">
			<div
				className="flex items-center btn-dark justify-between  p-4 cursor-pointer"
				onClick={() => toggleSection(title)}
			>
				<h2 className="text-lg font-bold">{title}</h2>
				<div className="transition-transform duration-300">
					{activeSections.includes(title) ? (
						<FaAngleUp size={18} />
					) : (
						<FaAngleDown size={18} />
					)}
				</div>
			</div>
			<div
				className={`bg-[#a2cffc] transition-all duration-300 ease-in-out overflow-hidden ${
					activeSections.includes(title)
						? 'max-h-[2000px] opacity-100'
						: 'max-h-0 opacity-0'
				}`}
			>
				{content}
			</div>
		</div>
	);

	return (
		<div className="sticky top-0 flex overflow-visible text-[#324B69]">
			<div
				className={`w-full pb-2 bg-[#324B69] shadow-lg transition-transform duration-300 ease-in-out transform ${
					isOpen ? 'translate-x-0' : '-translate-x-full'
				}  md:h-screen`}
			>
				<div className="h-full overflow-y-auto">
					{renderSection(
						'PROPERTY INFO',
						<div className="p-6 space-y-3">
							{propertyInfoTab.map(renderPropertyItem)}
						</div>
					)}
					{renderSection(
						'RENT STABILIZATION',
						<div className="p-6 space-y-3 text-[13px]">
							{yearlyUnits.length > 0 ? (
								<>
									<div className="drawer-item-div">
										<span className="drawer-text">
											Stabilized Units (most recent):
										</span>
										<span>{unitsrentstabilized}</span>
									</div>
									<div className="drawer-item-div">
										<span className="drawer-text">Change since 2007:</span>
										<span>
											{getPercentageDifference(
												yearlyUnits.at(-1)?.units || 0,
												unitsrentstabilized
											)}
										</span>
									</div>
									<div className="mt-4">
										<span className="font-semibold drawer-text">
											# STABILIZED UNITS
										</span>
										<div className="mt-4">
											{yearlyUnits.map(({ year, units }, index) => (
												<div key={index} className="pl-[20px] space-x-10">
													<span>{year}</span>
													<span>{units}</span>
												</div>
											))}
										</div>
									</div>
								</>
							) : (
								<span>No Rent Stabilization History</span>
							)}
						</div>
					)}

					{renderSection(
						'OWNERSHIP',
						<div className="p-6 space-y-3 text-[13px]">
							{hpdregistrations.length > 0 ? (
								<OwnerShip
									contacts={contacts}
									lastregistrationdate={formatDate(
										hpdregistrations[0]?.lastregistrationdate
									)}
									ownername={ownername}
									ownershipType={
										hpdregistrations[0]?.contacts[0]?.contactdescription
									}
								/>
							) : (
								<span>No Ownership History</span>
							)}
						</div>
					)}
					{renderSection(
						'PROGRAMS/STATUSES',
						<div className="p-6 drawer-item-div">
							<div>
								<span className="drawer-text mb-2">Subsidy Programs</span>
							</div>
							<span>None</span>
						</div>
					)}
					{renderSection(
						'ZONING',
						<div className="p-6 space-y-3">
							<div className="drawer-item-div">
								<span className="drawer-text">Zoning District(s)</span>
								<span>{zonedist1 || 'none'}</span>
							</div>
							<div className="drawer-item-div">
								<span className="drawer-text">Overlay(s)</span>
								<span>{overlay1 || 'none'}</span>
							</div>
							<div className="drawer-item-div">
								<span className="drawer-text">Special District(s)</span>
								<span>{spdist1 || 'none'}</span>
							</div>
							<div className="drawer-item-div">
								<span className="drawer-text">
									Built Floor Area Ratio (FAR)
								</span>
								<span>{builtfar}</span>
							</div>
							<div className="drawer-item-div">
								<span className="drawer-text">Maximum Residential FAR</span>
								<span>{residfar}</span>
							</div>
							<div className="drawer-item-div">
								<span className="drawer-text">Maximum Commercial FAR</span>
								<span>{commfar}</span>
							</div>
							<div className="drawer-item-div">
								<span className="drawer-text">
									Maximum Community Facility FAR
								</span>
								<span>{facilfar}</span>
							</div>
						</div>
					)}
					{renderSection(
						'LOCATION',
						<div className="p-2 flex justify-center items-center">
							<div className="h-[350px] w-full">
								{/* <FaMapPin size={32} className="text-gray-400" /> */}
								{address ? (
									<div>
										<DistrictMap locations={locations} />
									</div>
								) : (
									<span className="">Map will be rendered here</span>
								)}
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Toggle Button */}
			<button
				onClick={toggleDrawer}
				className={`
                     hidden md:block absolute left-[333px] top-12 -translate-y-1/2 btn-dark py-7 rounded-r-lg transition-all duration-300 ease-in-out ${
												isOpen ? 'translate-x-0' : '-translate-x-[333px]'
											}`}
			>
				<div className="transition-transform duration-300 ease-in-out">
					{isOpen ? <LuChevronLeft size={24} /> : <LuChevronRight size={24} />}
				</div>
			</button>
		</div>
	);
};
